// src/pages/marketplace/developer/DeveloperReviewsPage.tsx
// View product reviews for the developer portal.

import { useEffect, useState, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useProduct, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardContent,
  ErrorAlert,
} from '@react/components';
import { StatusBadge } from '../../../components/StatusBadge';

const TABS = [
  { key: 'edit', label: 'General', path: (id: string) => `/developer/products/${id}/edit` },
  { key: 'marketplace', label: 'Marketplace', path: (id: string) => `/developer/products/${id}/marketplace` },
  { key: 'dependencies', label: 'Dependencies', path: (id: string) => `/developer/products/${id}/dependencies` },
  { key: 'reviews', label: 'Reviews', path: (id: string) => `/developer/products/${id}/reviews` },
  { key: 'publish', label: 'Publish', path: (id: string) => `/developer/products/${id}/publish` },
] as const;

const REVIEWS_PER_PAGE = 10;

/** Render star icons for a rating value (0-5). */
function StarDisplay({ value, size = 'md' }: { value: number; size?: 'sm' | 'md' | 'lg' }) {
  const sizeClass = size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-6 h-6' : 'w-4 h-4';
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`${sizeClass} ${star <= value ? 'text-amber-500 fill-current' : 'text-muted-foreground/30 fill-current'}`}
          viewBox="0 0 20 20"
        >
          <path d="M10 15l-5.878 3.09 1.123-6.545L.49 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
        </svg>
      ))}
    </span>
  );
}

function reviewStatusVariant(status: string): string {
  switch (status) {
    case 'APPROVED': return 'success';
    case 'PENDING': return 'warning';
    case 'FLAGGED': return 'error';
    default: return 'secondary';
  }
}

export const DeveloperReviewsPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const store = useProduct();
  const { t } = useI18n();
  const [reviews, setReviews] = useState<any[]>([]);
  const [totalReviews, setTotalReviews] = useState(0);

  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  useEffect(() => {
    if (id) {
      store.GetProduct(id);
      loadReviews();
    }
    return () => {
      store.clearCurrent();
    };
  }, [id, currentPage]);

  const loadReviews = async () => {
    if (!id) return;
    try {
      const result = await store.ListProductReviews(id);
      const items = (result as any)?.items ?? (result as any)?.reviews ?? [];
      setReviews(items);
      setTotalReviews((result as any)?.total ?? items.length);
    } catch {
      // Error handled by store
    }
  };

  const stats = useMemo(() => {
    if (reviews.length === 0) {
      return {
        average: 0,
        total: totalReviews || 0,
        distribution: [0, 0, 0, 0, 0],
      };
    }
    const distribution = [0, 0, 0, 0, 0]; // 1-star through 5-star
    let sum = 0;
    for (const review of reviews) {
      const rating = review.rating ?? 0;
      sum += rating;
      if (rating >= 1 && rating <= 5) {
        distribution[rating - 1]++;
      }
    }
    return {
      average: reviews.length > 0 ? sum / reviews.length : 0,
      total: totalReviews || reviews.length,
      distribution,
    };
  }, [reviews, totalReviews]);

  const maxDistribution = Math.max(...stats.distribution, 1);

  const totalPages = Math.max(1, Math.ceil(stats.total / REVIEWS_PER_PAGE));

  const handlePageChange = (page: number) => {
    const params: Record<string, string> = {};
    if (page > 1) params.page = String(page);
    setSearchParams(params);
  };

  // Paginate locally for display
  const paginatedReviews = reviews.slice(
    (currentPage - 1) * REVIEWS_PER_PAGE,
    currentPage * REVIEWS_PER_PAGE,
  );

  const productName = (store.currentItem as any)?.name || 'Product';

  if (store.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div>
        <nav className="text-sm mb-2">
          <Link to="/developer" className="text-muted-foreground hover:text-foreground">
            Developer
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to={`/developer/products/${id}/edit`} className="text-muted-foreground hover:text-foreground">
            {productName}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">Reviews</span>
        </nav>
        <h1 className="text-2xl font-bold text-foreground">Reviews</h1>
      </div>

      {/* Tab navigation */}
      <nav className="flex border-b border-border">
        {TABS.map((tab) => (
          <Link
            key={tab.key}
            to={tab.path(id || '')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab.key === 'reviews'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
            }`}
          >
            {tab.label}
          </Link>
        ))}
      </nav>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Stats row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Average rating */}
        <Card>
          <CardContent className="p-6 flex flex-col items-center justify-center">
            <p className="text-5xl font-bold text-foreground">{stats.average.toFixed(1)}</p>
            <div className="mt-2">
              <StarDisplay value={Math.round(stats.average)} size="lg" />
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              {stats.total} {stats.total === 1 ? 'review' : 'reviews'}
            </p>
          </CardContent>
        </Card>

        {/* Distribution bars */}
        <Card className="lg:col-span-2">
          <CardContent className="p-6">
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((star) => {
                const count = stats.distribution[star - 1];
                const pct = maxDistribution > 0 ? (count / maxDistribution) * 100 : 0;
                return (
                  <div key={star} className="flex items-center gap-3">
                    <div className="flex items-center gap-1 w-12 justify-end">
                      <span className="text-sm font-medium text-foreground">{star}</span>
                      <svg className="w-3.5 h-3.5 text-amber-500 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.49 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    </div>
                    <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-500 rounded-full transition-all duration-300"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-sm text-muted-foreground w-8 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reviews list */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">All Reviews</h2>
        {paginatedReviews.length === 0 ? (
          <Card>
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground text-center">
                No reviews yet. Reviews will appear here once users start rating your product.
              </p>
            </CardContent>
          </Card>
        ) : (
          paginatedReviews.map((review: any) => {
            const reviewId = review.entity?.id || review.id || Math.random();
            return (
              <Card key={reviewId}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap mb-1">
                        <span className="text-sm font-semibold text-foreground">
                          {review.author_name || review.author?.name || 'Anonymous'}
                        </span>
                        <StarDisplay value={review.rating ?? 0} size="sm" />
                        <StatusBadge
                          variant={reviewStatusVariant(review.status ?? '')}
                          label={review.status || 'PENDING'}
                          size="sm"
                        />
                      </div>
                      {review.title && (
                        <p className="text-sm font-medium text-foreground mt-1">{review.title}</p>
                      )}
                      {review.body && (
                        <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                          {review.body}
                        </p>
                      )}
                      {review.created_at && (
                        <p className="text-xs text-muted-foreground mt-2">
                          {new Date(review.created_at).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              Previous
            </Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const page = i + 1;
              return (
                <Button
                  key={page}
                  variant={page === currentPage ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </Button>
              );
            })}
            {totalPages > 5 && (
              <span className="text-sm text-muted-foreground self-center">...</span>
            )}
            <Button
              variant="secondary"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});

export default DeveloperReviewsPage;
